/**
 * Rule sentences in the browser — a copy of backend/rules/catalogue.py's
 * render/sentence, used only to preview a rule while it is being edited.
 * The backend writes the sentences players see; keep the two in step.
 *
 * Template syntax: {param}, [optional part], [?param: shown if true],
 * [!param: shown if false], [=param=value: shown if equal].
 */

const TOKEN = /\{(\w+)\}/g;
const COND = /^([?!])(\w+):([\s\S]*)$/;
const EQ = /^=(\w+)=([\w-]+):([\s\S]*)$/;
const PLURAL_AFTER_ONE = /^(\s(?:extra |different |consecutive |dot |bonus )?)(runs|points|matches|overs|wickets|warnings|players|batters|bowlers|balls|minutes|teams)\b/;

const fmtNum = (v) => (typeof v === 'number' && Number.isInteger(v) ? String(v) : String(v));

const andJoin = (words) => (words.length <= 1 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

export function display(spec, value) {
  if (value === null || value === undefined) return '';
  switch (spec.type) {
    case 'bool': {
      const labels = spec.labels || { true: 'yes', false: 'no' };
      return labels[value ? 'true' : 'false'];
    }
    case 'enum':
      return spec.options?.[value] ?? String(value);
    case 'enum_list':
    case 'ordered_enum_list':
      return (value || []).map((v) => spec.options?.[v] ?? v).join(spec.join || ', ');
    case 'int_or':
    case 'number_or':
      if (typeof value === 'string') return spec.special?.[value] ?? value;
      return (spec.int_label || '{}').replace('{}', fmtNum(value));
    case 'over_list':
      return andJoin((value || []).map((v) => (typeof v === 'string' ? spec.special?.[v] ?? v : (spec.int_label || '{}').replace('{}', v))));
    case 'count_map':
      return andJoin(Object.entries(value || {}).map(([k, n]) => `${n} ${k}`));
    default:
      return fmtNum(value);
  }
}

function splitGroups(template) {
  const parts = [];
  let buf = '';
  let depth = 0;
  for (const ch of template) {
    if (ch === '[' && depth === 0) {
      if (buf) parts.push(buf);
      buf = '[';
      depth = 1;
    } else if (ch === ']' && depth === 1) {
      parts.push(`${buf}]`);
      buf = '';
      depth = 0;
    } else {
      buf += ch;
    }
  }
  if (buf) parts.push(buf);
  return parts;
}

function textSegments(text, params, spec, group) {
  const segs = [];
  let pos = 0;
  text.replace(TOKEN, (m, name, offset) => {
    if (offset > pos) segs.push({ text: text.slice(pos, offset), group });
    const value = params[name];
    const missing = value === null || value === undefined;
    segs.push({ param: name, value, text: spec[name] ? display(spec[name], value) : '', missing, group });
    pos = offset + m.length;
    return m;
  });
  if (pos < text.length) segs.push({ text: text.slice(pos), group });
  return segs;
}

/** The rule as segments: {text} or {param, value, text, missing}, each with `show`. */
export function render(block, rule) {
  if (!block || rule.block === 'text') return [{ text: rule.source_text || '', show: true }];
  const params = rule.params || {};
  const spec = block.params || {};
  const out = [];
  splitGroups(block.sentence || '').forEach((part, gi) => {
    if (!(part.startsWith('[') && part.endsWith(']'))) {
      textSegments(part, params, spec, null).forEach((s) => out.push({ ...s, show: true }));
      return;
    }
    const inner = part.slice(1, -1);
    const eq = inner.match(EQ);
    const cond = inner.match(COND);
    if (eq) {
      const [, p, want, body] = eq;
      textSegments(body, params, spec, gi).forEach((s) => out.push({ ...s, show: params[p] === want, condition: p }));
    } else if (cond) {
      const [, sign, p, body] = cond;
      const v = params[p];
      const show = sign === '?' ? !!v : v === false;
      textSegments(body, params, spec, gi).forEach((s) => out.push({ ...s, show, condition: p }));
    } else {
      const segs = textSegments(inner, params, spec, gi);
      const show = segs.every((s) => !('param' in s) || !s.missing);
      segs.forEach((s) => out.push({ ...s, show }));
    }
  });
  for (let i = 1; i < out.length; i += 1) {
    const prev = out[i - 1];
    if ('param' in prev && !('param' in out[i]) && prev.value === 1) {
      out[i] = { ...out[i], text: out[i].text.replace(PLURAL_AFTER_ONE, (m, a, w) => a + w.slice(0, -1)) };
    }
  }
  return out;
}

/** One plain sentence, as players read it. */
export function sentence(block, rule, stageLabels) {
  let text = render(block, rule).filter((s) => s.show).map((s) => s.text).join('').trim();
  text = text.replace(/\s{2,}/g, ' ').replace(/\s+([,;.])/g, '$1').replace(/—\s*;\s*/g, '— ');
  if (text && text[0] === text[0].toLowerCase()) text = text[0].toUpperCase() + text.slice(1);
  if (rule.stages && rule.stages.length && stageLabels) {
    text += ` (${andJoin(rule.stages.map((s) => stageLabels[s]))} only)`;
  }
  return text;
}

/** Names of required values still missing. */
export function missingRequired(block, params) {
  if (!block) return [];
  return Object.entries(block.params || {})
    .filter(([name, s]) => s.required && (params?.[name] === null || params?.[name] === undefined))
    .map(([name, s]) => s.label || name);
}

export const KIND_META = {
  scoring: { icon: 'ph-calculator', label: 'Scoring applies it', color: '#1F7A75', bg: '#E6F5F4' },
  standings: { icon: 'ph-ranking', label: 'Standings use it', color: '#1F7A75', bg: '#E6F5F4' },
  selection: { icon: 'ph-list-checks', label: 'Squad check', color: '#2B3A67', bg: '#E9EBF1' },
  umpire: { icon: 'ph-gavel', label: 'Umpire button', color: '#B3263F', bg: '#FCE8EC' },
  text: { icon: 'ph-file-text', label: 'Rulebook only', color: '#6B7280', bg: '#F3F4F6' },
};

export const SECTION_ACCENT = {
  match_format: '#3AAFA9',
  batting: '#E54562',
  bowling: '#F4D341',
  fielding: '#3AAFA9',
  points: '#2B3A67',
  penalties: '#E54562',
  general: '#9CA3AF',
};
